import { useState, useEffect } from "react";
import { PutObjectCommand, DeleteObjectCommand } from "@aws-sdk/client-s3";
import { r2Client, R2_BUCKET_NAME, R2_PUBLIC_URL } from "../r2";

export default function AdminGallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");

  // Fetch the current list of images from the public metadata.json
  const fetchImages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${R2_PUBLIC_URL}/metadata.json?t=${Date.now()}`); // cache buster
      if (!response.ok) {
        if (response.status === 404) {
          // metadata.json doesn't exist yet, start with empty array
          setImages([]);
          return;
        }
        throw new Error("Failed to fetch metadata");
      }
      const data = await response.json();
      setImages(data || []);
    } catch (error) {
      console.error("Error fetching images:", error);
      setImages([]); // Default to empty if error (e.g., first run)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  // Helper to save the updated metadata.json back to R2
  const saveMetadata = async (newImagesList) => {
    const jsonString = JSON.stringify(newImagesList);
    const command = new PutObjectCommand({
      Bucket: R2_BUCKET_NAME,
      Key: "metadata.json",
      Body: jsonString,
      ContentType: "application/json",
    });
    await r2Client.send(command);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadMessage("");
    try {
      // 1. Upload the actual file to R2
      const uniqueName = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      // Convert File to Uint8Array to prevent AWS SDK stream reading errors in the browser
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const command = new PutObjectCommand({
        Bucket: R2_BUCKET_NAME,
        Key: uniqueName,
        Body: uint8Array,
        ContentType: file.type,
      });
      await r2Client.send(command);

      // 2. Append the new file to our metadata list
      const publicUrl = `${R2_PUBLIC_URL}/${uniqueName}`;
      const newImage = { name: uniqueName, url: publicUrl, type: file.type };
      const updatedList = [newImage, ...images];
      
      // 3. Save the new list back to R2
      await saveMetadata(updatedList);
      
      setImages(updatedList);
      setUploadMessage("File uploaded to R2 successfully! 🎉");
    } catch (error) {
      console.error("Upload error:", error);
      setUploadMessage("Failed to upload file. Check your .env configuration.");
    } finally {
      setUploading(false);
      setTimeout(() => setUploadMessage(""), 3000);
    }
  };

  const handleDelete = async (imageToDelete) => {
    if (!window.confirm("Are you sure you want to delete this file permanently from R2?")) return;
    
    try {
      // 1. Delete from R2 bucket
      const command = new DeleteObjectCommand({
        Bucket: R2_BUCKET_NAME,
        Key: imageToDelete.name,
      });
      await r2Client.send(command);

      // 2. Remove from metadata list and save
      const updatedList = images.filter((img) => img.name !== imageToDelete.name);
      await saveMetadata(updatedList);
      
      setImages(updatedList);
      setUploadMessage("File deleted successfully. 🗑️");
    } catch (error) {
      console.error("Delete error:", error);
      setUploadMessage("Failed to delete file.");
    } finally {
      setTimeout(() => setUploadMessage(""), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-bgWarm p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Security Warning Banner */}
        <div className="bg-red-600 text-white font-bold text-center py-2 px-4 rounded-xl mb-6 shadow-lg animate-pulse">
          ⚠️ LOCAL ADMIN MODE ACTIVE. DO NOT DEPLOY THIS PAGE OR YOUR .ENV SECRETS TO PRODUCTION. ⚠️
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-10 bg-white/40 backdrop-blur-md rounded-2xl p-6 shadow-md border border-white/50">
          <div>
            <h1 className="text-3xl font-bold font-Philosopher text-heads">R2 Gallery Admin</h1>
            <p className="text-heads/70 font-medium">Manage your Cloudflare R2 Bucket</p>
          </div>
          <div className="mt-4 md:mt-0 flex gap-4">
            <a href="/gallery" target="_blank" rel="noreferrer" className="px-6 py-2 bg-gray-800 text-white rounded-lg font-bold hover:bg-black transition shadow-lg">
              View Live Gallery
            </a>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white/40 backdrop-blur-md rounded-2xl p-8 shadow-md border border-white/50 mb-10 text-center">
          <h2 className="text-2xl font-bold text-heads mb-4">Upload New Media</h2>
          
          <label className="cursor-pointer inline-block bg-gradient-to-r from-main to-accent text-white font-bold py-4 px-10 rounded-xl shadow-lg hover:shadow-2xl hover:-translate-y-1 transition duration-300">
            {uploading ? (
               <span className="flex items-center gap-2">
                 <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                 Uploading to R2...
               </span>
            ) : "Select Media to Upload"}
            <input 
              type="file" 
              accept="image/*,video/*" 
              className="hidden" 
              onChange={handleUpload}
              disabled={uploading}
            />
          </label>
          
          {uploadMessage && (
            <p className="mt-4 font-bold text-green-700 bg-green-100 py-2 px-4 rounded-lg inline-block">{uploadMessage}</p>
          )}
        </div>

        {/* Current Images Grid */}
        <h2 className="text-2xl font-bold text-heads mb-6">Manage R2 Bucket Images</h2>
        
        {loading ? (
          <div className="text-center py-20">Fetching from R2...</div>
        ) : images.length === 0 ? (
          <div className="text-center py-20 bg-white/30 rounded-2xl border border-white">No images in R2 bucket yet.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {images.map((img, index) => {
              const isVideo = img.name ? img.name.match(/\.(mp4|webm|ogg|mov)$/i) : false;
              return (
              <div key={img.name || index} className="group relative bg-white rounded-xl shadow-md overflow-hidden aspect-square border border-gray-200">
                {isVideo ? (
                  <video src={img.url} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                )}
                
                {/* Overlay with Delete Button */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <button 
                    onClick={() => handleDelete(img)}
                    className="bg-red-500 text-white p-3 rounded-full hover:bg-red-600 hover:scale-110 transition shadow-lg"
                    title="Delete permanently"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
                
                {isVideo && (
                  <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded shadow">
                    Video
                  </div>
                )}
              </div>
            )})}
          </div>
        )}

      </div>
    </div>
  );
}
